import React from 'react';
import { reportError } from '../utils/sentry';

/**
 * Reusable React Error Boundary component (class-based, required by React).
 *
 * Props:
 *   - children: React nodes to wrap
 *   - fallback: Component to render on error. Receives { error, reset } props.
 *   - onError: Optional callback (error, errorInfo) for logging/reporting
 *   - name: Optional name for identifying this boundary in logs
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    const name = this.props.name || 'ErrorBoundary';
    console.error(`[${name}]`, error, errorInfo.componentStack);

    // Report to Sentry
    reportError(error, { boundary: name }, errorInfo);

    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  reset() {
    this.setState({ error: null });
  }

  render() {
    if (this.state.error) {
      const FallbackComponent = this.props.fallback;

      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} reset={this.reset} name={this.props.name} />;
      }

      // Default minimal fallback if no fallback prop provided
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: 'var(--destructive, #dc2626)',
          backgroundColor: 'var(--card, #fff)',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 'var(--radius, 8px)',
          margin: '8px'
        }}>
          <p style={{ fontWeight: 600 }}>Something went wrong</p>
          <button
            onClick={this.reset}
            style={{
              marginTop: '8px',
              padding: '6px 16px',
              backgroundColor: 'var(--primary, #2563eb)',
              color: 'var(--primary-foreground, #fff)',
              border: 'none',
              borderRadius: 'var(--radius, 6px)',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
